// pages/SingleUser.js
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api from "../utils/api.js";
import { URLS, ImgURL } from "../utils/urls.js";
import Tooltip from "../components/Tooltip.jsx";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  User,
  CircleStar,
  BookmarkX,
  CircleEllipsis,
  Camera,
  Users,
  X,
  CreditCard 
} from "lucide-react";
import { useTopLoader } from "../context/TopLoaderContext";

const SingleUser = () => {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("media"); // default tab
  const [previewImage, setPreviewImage] = useState(null); // image preview state
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [quizResult, setQuizResult] = useState(null);
  const [customExtendDays, setCustomExtendDays] = useState("");
  const { start, stop } = useTopLoader();

  const handleSubscriptionAction = async (action) => {
    start();
  try {
    const res = await api.post(URLS.ADMIN_MANAGE_SUBSCRIPTION, {
      userId: user.id,
      action,
    });
    toast.success(res.data.message || "Action completed");

    // Refresh user data after action
    const refreshed = await api.get(URLS.GET_SINGLE_USER(userId));
    setUser(refreshed.data);
  } catch (err) {
    console.error(err);
    toast.error("Action failed");
  } finally {
    stop();
  }
};

const extendSubscription = async (days) => {
  try {
    const res = await api.post(URLS.ADMIN_MANAGE_SUBSCRIPTION, {
      userId: user.id,
      action: "extend",
      extendDays: days,
    });
    toast.success(res.data.message || "Extended");

    const refreshed = await api.get(URLS.GET_SINGLE_USER(userId));
    setUser(refreshed.data);
  } catch (err) {
    console.error(err);
    toast.error("Failed to extend subscription");
  }
};

  // ✅ Fetch user details
  useEffect(() => {
    const fetchUser = async () => {
      start()
      try {
        setLoading(true);
        const res = await api.get(URLS.GET_SINGLE_USER(userId));
        setUser(res.data);
        console.log("User Data:", res.data);
      } catch (err) {
        console.error("Failed to fetch user:", err);
        setError("Failed to load user details.");
      } finally {
        setLoading(false);
        stop()
      }
    };



    fetchUser();

  }, [userId]);

  // ✅ Fetch connections when "connections" tab is active
  useEffect(() => {
    const fetchConnections = async () => {
      if (activeTab !== "connections") return;
      try {
        setLoadingConnections(true);
        const res = await api.get(URLS.GET_SINGLE_USER_FRIENDS(userId));
        setConnections(res.data.friends || []);
      } catch (err) {
        console.error("Failed to fetch connections:", err);
      } finally {
        setLoadingConnections(false);
      }
    };
    
    const fetchUserQuiz = async () => {
      if (activeTab !== "quiz") return;
      try {
        const res = await api.get(`${URLS.GET_USER_QUIZ_RESULT(userId)}`);
        setQuizResult(res.data);
      } catch (err) {
        console.error("Failed to fetch quiz result:", err);
      }
    };

    if (activeTab === "connections") fetchConnections();
    if (activeTab === "quiz") fetchUserQuiz();
  }, [activeTab, userId]);

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500 bg-gray-100 min-h-screen">
        Loading user details...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center text-red-500 bg-gray-100 min-h-screen">
        {error}
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-8 text-center text-gray-500 bg-gray-100 min-h-screen">
        User not found.
      </div>
    );
  }

  const isActive = user.subscriptionStatus === "active";
const isCanceled = user.subscriptionStatus === "canceled";
const hasStripeSub = !!user.stripeSubscriptionId;

  return (
    <div className="p-6 bg-gray-100 min-h-screen font-sans">
      {/* Header */}
      <div className="flex items-center gap-6 mb-6">
        {user.profilePhotoUrl ? (
          <img
            src={ImgURL(user.profilePhotoUrl)}
            alt={user.firstName}
            className="h-20 w-20 rounded-full object-cover border"
          />
        ) : (
          <div className="h-20 w-20 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
            <User size={30} />
          </div>
        )}

        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            {`${user.firstName || ""} ${user.lastName || ""}`.trim() ||
              user.username}

            {user.subscriptionStatus && (
              <Tooltip text="Subscribed User">
                <CircleStar size={20} color="green" />
              </Tooltip>
            )}

            {user?.isBlocked === 1 && (
              <Tooltip text="Blocked User">
                <BookmarkX size={20} color="red" />
              </Tooltip>
            )}

            {user.isVerified === 0 && (
              <Tooltip text="Not Verified">
                <CircleEllipsis size={20} color="orange" />
              </Tooltip>
            )}
          </h2>
          <p className="text-gray-600">{user.email}</p>
          <p className="text-gray-500">{user.locationCity || "Unknown Location"}</p>
        </div>
      </div>

      {/* Details Card */}
      <div className="bg-white rounded-2xl shadow p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">User Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
          <p><span className="font-medium">Username:</span> {user.username}</p>
          <p><span className="font-medium">Age:</span> {user.age || "N/A"}</p>
          <p><span className="font-medium">Gender:</span> {user.gender || "N/A"}</p>
          <p><span className="font-medium">Personality:</span> {user.personalityType || "N/A"}</p>
          <p><span className="font-medium">Joined:</span> {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "-"}</p>
          <p><span className="font-medium">Last Active:</span> {user.lastActiveAt ? new Date(user.lastActiveAt).toLocaleString() : "N/A"}</p>
        </div>

        {user.interests?.length > 0 && (
          <div className="mt-4">
            <span className="font-medium text-gray-800">Interests:</span>
            <div className="flex flex-wrap gap-2 mt-2">
              {user.interests.map((interest, i) => (
                <span
                  key={i}
                  className="px-3 py-1 text-sm rounded-full bg-[#db8654]/10 text-[#db8654]"
                >
                  {interest}
                </span>
              ))}
            </div>
          </div>
        )}

      </div>



{/* Tabs */}
<div className="bg-white rounded-2xl shadow">
  <div className="flex border-b">
    {[
      { key: "media", label: "Media", icon: Camera },
      { key: "connections", label: "Connections", icon: Users },
      { key: "quiz", label: "Personality Result", icon: CircleStar },
    ].map((tab) => (
      <button
        key={tab.key}
        onClick={() => setActiveTab(tab.key)}
        className={`flex items-center gap-2 px-6 py-3 font-medium ${
          activeTab === tab.key
            ? "border-b-2 border-[#db8654] text-[#db8654]"
            : "text-gray-500 hover:text-gray-700"
        }`}
      >
        <tab.icon size={18} /> {tab.label}
      </button>
    ))}
  </div>

  <div className="p-6">
    {/* Media Tab */}
    {activeTab === "media" && (
      <div className="grid grid-cols-2 md:grid-cols-8 gap-4">
        {user.media?.length > 0 ? (
          user.media.map((m) => (
            <img
              key={m.id}
              src={ImgURL(m.url)}
              alt="User Media"
              onClick={() => setPreviewImage(m.url)}
              className="rounded-xl object-cover w-full h-40 shadow cursor-pointer hover:opacity-90"
            />
          ))
        ) : (
          <p className="text-gray-500">No media uploaded.</p>
        )}
      </div>
    )}

    {/* Connections Tab */}
    {activeTab === "connections" && (
      <div>
        {loadingConnections ? (
          <p className="text-gray-500">Loading connections...</p>
        ) : connections.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {connections.map((connection) => (
              <Link
                key={connection.id}
                to={`/admin/users/${connection.id}`}
                className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl shadow-sm hover:shadow-md transition cursor-pointer"
              >
                {connection.profilePhotoUrl ? (
                  <img
                    src={ImgURL(connection.profilePhotoUrl)}
                    alt={connection.firstName}
                    className="h-12 w-12 rounded-full object-cover border"
                  />
                ) : (
                  <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                    <User size={20} />
                  </div>
                )}
                <div>
                  <p className="font-medium text-gray-800">
                    {connection.firstName} {connection.lastName}
                  </p>
                  <p className="text-sm text-gray-500">{connection.email}</p>
                  <p className="text-xs font-semibold text-[#db8654] mt-1">
                    {connection.personalityType || "No Personality"}
                  </p>
                  <div className="mt-2 space-y-1.5">
                    {/* Overall Match */}
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-gray-400 w-16">Match</span>
                        <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-orange-500" style={{ width: `${connection.matchScore}%` }}></div>
                        </div>
                        <span className="text-[10px] font-bold text-orange-600">{connection.matchScore}%</span>
                    </div>
                    {/* Personality Breakdown */}
                    <div className="flex items-center gap-2">
                        <span className="text-[9px] text-gray-400 w-16">Personality</span>
                        <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-400" style={{ width: `${connection.matchBreakdown?.personality || 0}%` }}></div>
                        </div>
                        <span className="text-[9px] text-gray-500">{connection.matchBreakdown?.personality || 0}%</span>
                    </div>
                    {/* Interests Breakdown */}
                    <div className="flex items-center gap-2">
                        <span className="text-[9px] text-gray-400 w-16">Interests</span>
                        <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-green-400" style={{ width: `${connection.matchBreakdown?.interests || 0}%` }}></div>
                        </div>
                        <span className="text-[9px] text-gray-500">{connection.matchBreakdown?.interests || 0}%</span>
                    </div>
                    {/* Lifestyle Breakdown */}
                    <div className="flex items-center gap-2">
                        <span className="text-[9px] text-gray-400 w-16">Lifestyle</span>
                        <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-purple-400" style={{ width: `${connection.matchBreakdown?.lifestyle || 0}%` }}></div>
                        </div>
                        <span className="text-[9px] text-gray-500">{connection.matchBreakdown?.lifestyle || 0}%</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No connections found.</p>
        )}
      </div>
    )}

{/* Quiz Result Tab */}
{activeTab === "quiz" && (
  <div>
    {quizResult?.data?.personalityType ? (
      <div className="space-y-6">
        {/* Personality Type */}
        <div
          className="p-6 rounded-xl shadow bg-gray-50"
          style={{ borderLeft: `6px solid ${quizResult.data.personalityType.color || "#db8654"}` }}
        >
          <h3 className="text-xl font-bold mb-2 text-gray-800">
            {quizResult.data.personalityType.title} ({quizResult.data.personalityType.code})
          </h3>
          <p className="text-sm opacity-90 mb-2 text-gray-600">
            {quizResult.data.personalityType.description}
          </p>
          <p className="text-sm italic text-gray-600 font-medium">"{quizResult.data.personalityType.motto}"</p>
        </div>

        {/* Who You Are */}
        <div className="p-4 bg-gray-50 rounded-xl shadow">
          <h4 className="font-semibold text-gray-800 mb-2">👋 Who You Are</h4>
          <p className="text-sm text-gray-600">
            {quizResult.data.personalityType.who_you_are}
          </p>
        </div>

        {/* Friendship Tip */}
        <div className="p-4 bg-gray-50 rounded-xl shadow">
          <h4 className="font-semibold text-gray-800 mb-2">✨ Friendship Tip</h4>
          <p className="text-sm text-gray-600">
            {quizResult.data.personalityType.friendship_tip}
          </p>
        </div>

        {/* Playlist Vibe */}
        <div className="p-4 bg-gray-50 rounded-xl shadow">
          <h4 className="font-semibold text-gray-800 mb-2">🎵 Playlist Vibe</h4>
          <p className="text-sm text-gray-600 italic">
            {quizResult.data.personalityType.playlist_vibe}
          </p>
        </div>

        {/* Traits */}  
        <div className="mb-8">
          <h4 className="text-md font-semibold text-gray-800 mb-4">
            📊 Personality Identity Scores
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            {quizResult.data.traits.map((trait, i) => (
              <div
                key={i}
                className="p-3 border rounded-xl bg-white shadow-sm flex flex-col items-center"
              >
                <p className="font-bold text-gray-500 uppercase text-[10px] mb-1">
                  {trait.name}
                </p>
                <p className="text-xl font-black text-[#db8654]">
                    {trait.totalScore}
                </p>
                {trait.internalScore !== 'N/A' && (
                    <p className="text-[9px] text-gray-400 mt-1">
                        I:{trait.internalScore} | E:{trait.externalScore}
                    </p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Category Breakdown */}
        {quizResult.data.categoryScores && (
            <div>
                <h4 className="text-md font-semibold text-gray-800 mb-4">
                    🔍 Quiz Section Breakdown
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(quizResult.data.categoryScores).map(([name, score], i) => (
                        <div key={i} className="p-4 bg-white border rounded-xl shadow-sm">
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-700">{name}</span>
                                <span className="text-lg font-bold text-[#db8654]">{score}</span>
                            </div>
                            <div className="w-full bg-gray-100 h-1.5 rounded-full mt-2 overflow-hidden">
                                <div 
                                    className="bg-indigo-400 h-full" 
                                    style={{ width: `${Math.min(100, (score / 15) * 100)}%` }}
                                ></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )}
      </div>
    ) : (
      <p className="text-gray-500">No quiz result available for this user.</p>
    )}
  </div>
)}
  </div>
</div>


      {/* Image Preview Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative">
            <img
              src={ImgURL(previewImage)}
              alt="Preview"
              className="max-h-[80vh] max-w-[90vw] rounded-xl shadow-lg"
            />
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute top-2 right-2 bg-white rounded-full p-1 shadow hover:bg-gray-100"
            >
              <X size={24} className="text-gray-700" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SingleUser;
